import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Plus, Search, FileText, MoreVertical, Eye, Edit2, Trash2,
  CheckCircle, XCircle, Download, Mail, MessageCircle, IndianRupee
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import { debitNotesAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const STATUS_COLORS = {
  DRAFT: 'bg-slate-100 text-slate-700',
  ISSUED: 'bg-blue-100 text-blue-700',
  ADJUSTED: 'bg-emerald-100 text-emerald-700',
  CANCELLED: 'bg-red-100 text-red-700'
};

const REASON_LABELS = {
  GOODS_RETURNED: 'Goods Returned',
  PRICE_DIFFERENCE: 'Price Difference',
  QUALITY_ISSUE: 'Quality Issue',
  SHORTAGE: 'Shortage',
  DAMAGE: 'Damaged Goods',
  OTHER: 'Other',
};

const DebitNotesPage = () => {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const { activeBusiness } = useBusiness();
  const [notes, setNotes] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  const bid = businessId || activeBusiness?.id;

  const fetchDebitNotes = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;
      const response = await debitNotesAPI.list(params);
      const data = response.data;
      setNotes(data.debit_notes || []);
      if (data.stats) setStats(data.stats);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load debit notes'));
    } finally {
      setLoading(false);
    }
  }, [bid, statusFilter, searchQuery]);

  useEffect(() => {
    fetchDebitNotes();
  }, [fetchDebitNotes]);

  const handleView = (note) => {
    navigate(`/business/${bid}/debit-notes/${note.id}`);
  };

  const handleEdit = (note) => {
    navigate(`/business/${bid}/debit-notes/${note.id}/edit`);
  };

  const handleIssue = (note) => {
    setConfirmModal({
      title: 'Issue Debit Note',
      description: `Issue debit note ${note.note_number}? This will mark it as ready for supplier.`,
      confirmLabel: 'Issue',
      confirmClass: 'bg-teal-600 hover:bg-teal-700',
      icon: CheckCircle,
      iconBgClass: 'bg-teal-100',
      iconClass: 'text-teal-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await debitNotesAPI.issue(note.id);
          toast.success('Debit note issued');
          fetchDebitNotes();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error issuing debit note'));
        }
      },
    });
  };

  const handleCancel = (note) => {
    setCancelReason('');
    setConfirmModal({
      title: 'Cancel Debit Note',
      description: `Cancel debit note ${note.note_number}? This action cannot be undone.`,
      confirmLabel: 'Cancel Note',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: XCircle,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      isCancelFlow: true,
      onConfirm: async () => {
        const reason = document.getElementById('cancel-reason-input')?.value || '';
        setConfirmModal(null);
        try {
          await debitNotesAPI.cancel(note.id, reason);
          toast.success('Debit note cancelled');
          fetchDebitNotes();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error cancelling debit note'));
        }
      },
    });
  };

  const handleDelete = (note) => {
    setConfirmModal({
      title: 'Delete Debit Note',
      description: `Delete debit note ${note.note_number}? This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await debitNotesAPI.delete(note.id);
          toast.success('Debit note deleted');
          fetchDebitNotes();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error deleting debit note'));
        }
      },
    });
  };

  const handleDownloadPdf = async (note) => {
    try {
      const response = await debitNotesAPI.pdf(note.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `DN_${note.note_number || 'debit-note'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error downloading PDF'));
    }
  };

  const getSupplierName = (n) => n.supplier_name || n.party_name || '-';
  const getAmount = (n) => Number(n.amount || n.grand_total || 0);
  const getReasonLabel = (n) => REASON_LABELS[n.reason] || n.reason_label || n.reason || '-';

  const renderActions = (note) => {
    const status = (note.status || '').toUpperCase();
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="p-1.5 hover:bg-slate-100 rounded-lg">
            <MoreVertical className="w-4 h-4 text-slate-500" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={() => handleView(note)}>
            <Eye className="w-4 h-4 mr-2" /> View
          </DropdownMenuItem>

          {status === 'DRAFT' && (
            <DropdownMenuItem onClick={() => handleEdit(note)}>
              <Edit2 className="w-4 h-4 mr-2" /> Edit
            </DropdownMenuItem>
          )}

          {status === 'DRAFT' && (
            <DropdownMenuItem onClick={() => handleIssue(note)}>
              <CheckCircle className="w-4 h-4 mr-2" /> Issue
            </DropdownMenuItem>
          )}

          <DropdownMenuItem onClick={() => handleDownloadPdf(note)}>
            <Download className="w-4 h-4 mr-2" /> Download PDF
          </DropdownMenuItem>

          {(status === 'DRAFT' || status === 'ISSUED') && (
            <>
              <DropdownMenuSeparator />
              {status === 'DRAFT' && (
                <DropdownMenuItem onClick={() => handleDelete(note)} className="text-red-600 focus:text-red-600">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </DropdownMenuItem>
              )}
              {status === 'ISSUED' && (
                <DropdownMenuItem onClick={() => handleCancel(note)} className="text-red-600 focus:text-red-600">
                  <XCircle className="w-4 h-4 mr-2" /> Cancel
                </DropdownMenuItem>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className="p-4 lg:p-6 space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Debit Notes</h1>
          <p className="text-sm text-slate-500">Manage debit notes to suppliers</p>
        </div>
        <button
          onClick={() => navigate(`/business/${bid}/debit-notes/new`)}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> New Debit Note
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Total</p>
          <p className="text-2xl font-bold text-slate-800">{stats.total || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Draft</p>
          <p className="text-2xl font-bold text-slate-600">{stats.draft || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Issued</p>
          <p className="text-2xl font-bold text-blue-600">{stats.issued || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Adjusted</p>
          <p className="text-2xl font-bold text-emerald-600">{stats.adjusted || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-1">
            <IndianRupee className="w-3 h-3 text-slate-400" />
            <p className="text-xs text-slate-500">Total Value</p>
          </div>
          <p className="text-2xl font-bold text-emerald-600">
            {'\u20B9'}{(stats.total_value || stats.total_amount || 0).toLocaleString('en-IN')}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by note number or supplier..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-white"
        >
          <option value="">All Status</option>
          <option value="DRAFT">Draft</option>
          <option value="ISSUED">Issued</option>
          <option value="ADJUSTED">Adjusted</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        ) : notes.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No debit notes found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" style={{ tableLayout: 'fixed' }}>
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500" style={{ width: '14%' }}>Note #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500" style={{ width: '20%' }}>Supplier</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500" style={{ width: '12%' }}>Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500" style={{ width: '16%' }}>Reason</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500" style={{ width: '14%' }}>Amount</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500" style={{ width: '12%' }}>Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500" style={{ width: '8%' }}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {notes.map((note) => (
                  <tr
                    key={note.id}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => handleView(note)}
                  >
                    <td className="px-4 py-3 font-medium text-blue-600 truncate">{note.note_number}</td>
                    <td className="px-4 py-3 text-slate-600 truncate">{getSupplierName(note)}</td>
                    <td className="px-4 py-3 text-slate-500 text-sm">{note.note_date}</td>
                    <td className="px-4 py-3 text-slate-500 text-sm truncate">{getReasonLabel(note)}</td>
                    <td className="px-4 py-3 font-medium text-slate-800 text-right">
                      {'\u20B9'}{getAmount(note).toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[(note.status || '').toUpperCase()] || STATUS_COLORS.DRAFT}`}>
                        {(note.status || 'DRAFT').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {renderActions(note)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer */}
      {notes.length > 0 && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>Showing {notes.length} debit note{notes.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconBgClass={confirmModal?.iconBgClass}
        iconClass={confirmModal?.iconClass}
        confirmDisabled={confirmModal?.isCancelFlow ? !cancelReason.trim() : false}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => { setConfirmModal(null); setCancelReason(''); }}
      >
        {confirmModal?.isCancelFlow && (
          <div className="mt-3">
            <label className="text-sm font-medium text-slate-700">Cancellation Reason</label>
            <textarea
              id="cancel-reason-input"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation..."
              className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
              rows={3}
            />
          </div>
        )}
      </ConfirmModal>
    </div>
  );
};

export default DebitNotesPage;
